import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  Timestamp,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';

import { firestore } from '../Firebase';
import { STORAGE_URL } from '../Firebase';

import { deleteFile, storageFileExists } from './storageApi';

export interface GameplayThemeData {
  id: string;
  name: string;
  previewUrl?: string | null;
  createdAt: Date;
  order: number;
  gameplayBackgroundColor?: string | null;
  gameplayBackgroundImageUrl?: string | null;
  boardBackgroundColor?: string | null;
  boardBackgroundImageUrl?: string | null;
  scrollerColor: string;
  buttonsBackgroundColor: string;
  buttonsIconsColor: string;
  buttonsActiveIconsColor?: string | null;
  gameplayBackgroundRepeatMode?: string | null;
}

export interface GameplayThemePayload {
  name: string;
  previewUrl?: string | null;
  createdAt: Timestamp;
  order: number;
  gameplayBackgroundColor?: string | null;
  gameplayBackgroundImageUrl?: string | null;
  boardBackgroundColor?: string | null;
  boardBackgroundImageUrl?: string | null;
  scrollerColor: string;
  buttonsBackgroundColor: string;
  buttonsIconsColor: string;
  buttonsActiveIconsColor?: string | null;
  gameplayBackgroundRepeatMode?: string | null;
}

const COLLECTION = 'gameplayThemes';

function docToTheme(id: string, data: Record<string, any>): GameplayThemeData {
  return {
    id,
    name: data.name,
    previewUrl: data.previewUrl ?? null,
    createdAt: data.createdAt?.toDate?.() ?? new Date(),
    order: data.order as number,
    gameplayBackgroundColor: data.gameplayBackgroundColor ?? null,
    gameplayBackgroundImageUrl: data.gameplayBackgroundImageUrl ?? null,
    boardBackgroundColor: data.boardBackgroundColor ?? null,
    boardBackgroundImageUrl: data.boardBackgroundImageUrl ?? null,
    scrollerColor: data.scrollerColor,
    buttonsBackgroundColor: data.buttonsBackgroundColor,
    buttonsIconsColor: data.buttonsIconsColor,
    buttonsActiveIconsColor: data.buttonsActiveIconsColor ?? null,
    gameplayBackgroundRepeatMode: data.gameplayBackgroundRepeatMode ?? null,
  };
}

export async function getAllThemes(): Promise<GameplayThemeData[]> {
  const q = query(collection(firestore, COLLECTION), orderBy('order', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => docToTheme(d.id, d.data()));
}

export async function getThemeById(id: string): Promise<GameplayThemeData | null> {
  const snap = await getDoc(doc(firestore, COLLECTION, id));
  if (!snap.exists()) return null;
  return docToTheme(snap.id, snap.data() as Record<string, any>);
}

export async function setThemeData(
  id: string,
  payload: GameplayThemePayload,
  isCreate: boolean,
): Promise<void> {
  const ref = doc(firestore, COLLECTION, id);
  if (isCreate) {
    await setDoc(ref, payload);
  } else {
    await updateDoc(ref, { ...payload });
  }
}

export async function deleteThemeById(id: string): Promise<void> {
  await deleteDoc(doc(firestore, COLLECTION, id));

  // Best-effort cleanup of storage assets
  const kinds = ['background', 'board', 'preview'] as const;
  await Promise.allSettled(
    kinds.map(async (kind) => {
      const path = `gameplayThemes/${id}/${kind}`;
      const exists = await storageFileExists(path as any).catch(() => false);
      if (exists) await deleteFile(path as any);
    }),
  );
}

export function buildThemeImageUrl(fullPath: string): string {
  return `${STORAGE_URL}/${fullPath}?timestamp=${Date.now()}`;
}

/**
 * Returns the next available order value (max existing order + 1, or 0 if empty).
 */
export async function getNextThemeOrder(): Promise<number> {
  const themes = await getAllThemes();
  if (themes.length === 0) return 0;
  return Math.max(...themes.map((t) => t.order)) + 1;
}

/**
 * Batch-updates the `order` field on a list of theme documents.
 */
export async function updateThemesOrder(updates: { id: string; order: number }[]): Promise<void> {
  const batch = writeBatch(firestore);
  for (const { id, order } of updates) {
    batch.update(doc(firestore, COLLECTION, id), { order });
  }
  await batch.commit();
}
